/**
 * Ringostat webhook panel (no connect/disconnect — webhook-only).
 *
 * Ringostat has no OAuth handshake: the webhook URL simply exists and is auto-provisioned
 * by GET /status. This panel shows that URL plus the exact field mapping the user must add
 * in Ringostat's Webhooks 2.0 builder — the JSON keys here match what the backend reads in
 * RingostatCallsService.buildCallRecord. Shares the `hub-ringostat` query key with the hub.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Loader2, Copy, Check, ChevronDown, ChevronRight } from "lucide-react";

interface RingostatStatus {
  connected: boolean;
  projectId?: string;
  webhookUrl?: string;
}

/** JSON keys the backend reads → what the user should map them to in the Ringostat builder. */
const FIELD_MAP: { key: string; meaning: string; required: boolean }[] = [
  { key: "call_id", meaning: "Unikalny ID połączenia (deduplikacja)", required: true },
  { key: "type", meaning: "Kierunek: in / out", required: true },
  { key: "status", meaning: "Status: answered / no answer / busy …", required: true },
  { key: "caller", meaning: "Numer dzwoniącego", required: true },
  { key: "callee", meaning: "Numer docelowy", required: true },
  { key: "date", meaning: "Data i czas połączenia (YYYY-MM-DD HH:MM:SS)", required: true },
  {
    key: "dialog",
    meaning: "Czas rozmowy w sekundach (liczy się jako czas rozmowy)",
    required: false,
  },
  { key: "call_duration", meaning: "Całkowity czas połączenia w sekundach", required: false },
  { key: "waiting", meaning: "Czas oczekiwania / dzwonienia w sekundach", required: false },
  { key: "manager", meaning: "Setter / pracownik obsługujący", required: false },
  { key: "record_link", meaning: "Link do nagrania", required: false },
];

export default function RingostatConnectPanel({ forUserId }: { forUserId?: string | null }) {
  const [copied, setCopied] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const statusKey = forUserId ? ["hub-ringostat", forUserId] : ["hub-ringostat"];
  const withForUser = (url: string) => (forUserId ? `${url}?forUserId=${forUserId}` : url);

  const { data, isLoading } = useQuery<RingostatStatus>({
    queryKey: statusKey,
    queryFn: () => apiRequest("GET", withForUser("/api/integrations/ringostat/status")),
    staleTime: 60_000,
    retry: false,
  });

  const handleCopy = async () => {
    if (!data?.webhookUrl) return;
    try {
      await navigator.clipboard.writeText(data.webhookUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — ignore */
    }
  };

  if (isLoading) {
    return <Loader2 className="w-4 h-4 animate-spin text-white/30" />;
  }

  return (
    <div className="space-y-3">
      {/* Webhook URL */}
      <div className="space-y-1.5">
        <p className="text-xs text-white/50">Krok 1 — skopiuj swój Webhook URL:</p>
        <div className="flex items-center gap-2">
          <input
            readOnly
            value={data?.webhookUrl ?? ""}
            onFocus={(e) => e.currentTarget.select()}
            className="flex-1 px-3 py-2 text-xs rounded-md bg-white/[0.04] border border-white/[0.1] text-white/80 font-mono"
          />
          <button
            onClick={handleCopy}
            disabled={!data?.webhookUrl}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-md border border-white/[0.1] hover:bg-white/[0.06] disabled:opacity-50 text-white/70 transition-colors"
          >
            {copied ? (
              <Check className="w-3.5 h-3.5 text-emerald-400" />
            ) : (
              <Copy className="w-3.5 h-3.5" />
            )}
            {copied ? "Skopiowano" : "Kopiuj"}
          </button>
        </div>
      </div>

      {/* Expandable setup guide */}
      <button
        onClick={() => setGuideOpen((o) => !o)}
        className="flex items-center gap-1.5 text-xs font-medium text-white/70 hover:text-white transition-colors"
      >
        {guideOpen ? (
          <ChevronDown className="w-3.5 h-3.5" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5" />
        )}
        Jak skonfigurować w Ringostat (krok po kroku)
      </button>

      {guideOpen && (
        <div className="space-y-3 rounded-lg border border-white/[0.08] bg-white/[0.02] p-3">
          <ol className="space-y-1.5 text-xs text-white/60 list-decimal list-inside leading-relaxed">
            <li>
              Ringostat →{" "}
              <span className="text-white/80">Ustawienia → Integracje → Webhooks 2.0</span> → „Dodaj
              webhook".
            </li>
            <li>
              Metoda: <span className="text-white/80">POST</span>, format:{" "}
              <span className="text-white/80">JSON</span>.
            </li>
            <li>
              Trigger: <span className="text-white/80">po zakończeniu połączenia</span>, dla
              połączeń <span className="text-white/80">przychodzących i wychodzących</span>.
            </li>
            <li>URL: wklej skopiowany wyżej Webhook URL.</li>
            <li>
              W treści (body) dodaj poniższe pola —{" "}
              <span className="text-white/80">nazwy kluczy muszą być dokładnie takie</span>:
            </li>
          </ol>

          <div className="rounded-md border border-white/[0.08] overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-white/[0.04] text-white/50">
                  <th className="text-left font-medium px-2.5 py-1.5">Klucz (key)</th>
                  <th className="text-left font-medium px-2.5 py-1.5">Co wysłać</th>
                  <th className="text-center font-medium px-2.5 py-1.5">Wymagane</th>
                </tr>
              </thead>
              <tbody>
                {FIELD_MAP.map((f) => (
                  <tr key={f.key} className="border-t border-white/[0.06]">
                    <td className="px-2.5 py-1.5 font-mono text-white/85">{f.key}</td>
                    <td className="px-2.5 py-1.5 text-white/60">{f.meaning}</td>
                    <td className="px-2.5 py-1.5 text-center">
                      {f.required ? (
                        <span className="text-emerald-400">✓</span>
                      ) : (
                        <span className="text-white/30">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-[11px] text-white/40 leading-relaxed">
            Każdemu kluczowi przypisz odpowiednią zmienną Ringostata (np. status połączenia, numer,
            czas rozmowy). Po zapisaniu połączenia zaczną się zliczać automatycznie w zakładce
            „Setter" na dashboardzie — nic więcej nie trzeba klikać.
          </p>
        </div>
      )}
    </div>
  );
}
